import { Box, CircularProgress, Collapse } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { useDebounce } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/hooks/useDebounce';
import { useGoogleMaps } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/hooks/useGoogleMaps';
import { Prediction } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/types';
import { useAddressContext } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/contexts/AddressContext';
import { useAddressFormFields } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/hooks/useAddressFormFields';
import { getFormFieldData } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/services/getFormFieldData';
import { AddressMode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/types/AddressMode';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { CustomTextField } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface GoogleMapsAutocompleteProps {
  subFieldName: string;
  apiKey: string;
  label?: string;
  placeholder?: string;
  language?: string;
  fieldVariant?: FieldVariant;
  debounceTime?: number;
}

export const GoogleMapsAutocomplete = ({
  subFieldName,
  apiKey,
  label,
  placeholder,
  language,
  fieldVariant = FieldVariant.form,
  debounceTime = 500,
}: GoogleMapsAutocompleteProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { palette } = useCustomTheme();
  const { control, formState } = useFormContext();
  const { fieldNamesPrefix, openedDialog, setMode, focusAutocomplete, setFocusStreetName } = useAddressContext();
  const { saveTemporaryCardData, updateFieldsFromGeocode, emptyFields, getValidation, forceAutocompleteError } =
    useAddressFormFields();
  const { debounce } = useDebounce();
  const { load, retrievePredictions, geocodePlace } = useGoogleMaps();

  const { getMUIAutocompleteFormClasses } = useCustomFieldStyles({
    fieldVariant: fieldVariant,
    disabled: false,
  });

  const [address, setAddress] = useState<string | null>(null);
  const [searching, setSearching] = useState<boolean | null>(null);
  const [predictions, setPredictions] = useState<Prediction[] | null>(null);
  const [inputIsDirty, setInputIsDirty] = useState(false);
  const [updatingFields, setUpdatingFields] = useState(false);

  useEffect(() => {
    load(apiKey, language);
  }, []);

  const autocompleteClasses = getMUIAutocompleteFormClasses();

  const name = `${fieldNamesPrefix}.${subFieldName}`;

  const onInputChange = (_, value) => {
    // guard for setValue outside dialog
    if (!openedDialog) return;

    let isDirty = inputIsDirty;

    // initial value
    if (!isDirty && value !== getFormFieldData(`${fieldNamesPrefix}.address`, formState.defaultValues)) {
      isDirty = true;
      setInputIsDirty(isDirty);
    }

    // cleared/empty/short value
    if (!value || value.length <= 3) {
      setPredictions(null);
      setSearching(false);
      setAddress(null);
      emptyFields();
      return;
    }

    // search string +3 chars
    setSearching(true);
    setAddress(value);
    debounce(async () => {
      try {
        const matches = await retrievePredictions(value);
        setPredictions(matches);
      } catch (err) {
        setPredictions(null);
      } finally {
        setSearching(false);
      }
    }, debounceTime);
  };

  const onSelect = async (_, option: string | null) => {
    if (option === undefined || option === null || option === '') return;

    // manual option is selected
    if (option === '0') {
      setUpdatingFields(false);
      setAddress(null);
      startManualMode();
      return;
    }

    // prediction is selected
    setUpdatingFields(true);

    const selectedOption = predictions?.find((p) => p.description === option);
    if (!selectedOption) return;
    const res = await geocodePlace(selectedOption.place_id);

    // UI update
    setTimeout(() => {
      updateFieldsFromGeocode(res);
      setPredictions(null);
      updateModeAfterSelect();
    }, 1000); // ensures user can see the loading animation
  };

  const startManualMode = (): void => {
    emptyFields();
    setMode(AddressMode.MANUAL);
    setTimeout(() => {
      setFocusStreetName(true);
    }, 500);
  };

  const updateModeAfterSelect = async () => {
    const areFieldsValid = await getValidation();
    if (areFieldsValid) {
      saveTemporaryCardData();
      setMode(AddressMode.CONFIRM);
    } else {
      setMode(AddressMode.EDIT);
    }
    // UI update
    setTimeout(() => {
      setUpdatingFields(false);
    }, 1000); // ensures user can see the loading animation
  };

  const getOptions = (): string[] => {
    if (searching || !address || !inputIsDirty) return [];

    const OPTION_NO_RESULTS = getTextIdTranslation('NO_OPTIONS_TO_DISPLAY');
    const OPTION_MANUAL = '0';

    return predictions && predictions.length > 0
      ? [...predictions.map((p) => p.description), OPTION_MANUAL]
      : [OPTION_NO_RESULTS, OPTION_MANUAL];
  };

  const renderOption = (option: string) => {
    if (option !== '0') {
      return option;
    }

    return (
      <Box
        display={'block'}
        borderTop={`1px solid ${palette.grey[300]}`}
        mt={'-6px'}
        boxSizing={'border-box'}
        width={'100%'}
      >
        <Box
          display={'flex'}
          color={palette.text.primary}
          flexDirection={'row'}
          alignItems={'center'}
          mt={1}
          gridGap={8}
        >
          <Font variant="body2" style={{ fontWeight: 400 }}>
            {getTextIdTranslation('COMPLETE_ADDRESS_MANUALLY')}
          </Font>
          <Icon size={16}>edit</Icon>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Collapse in={!updatingFields}>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => {
            return (
              <Autocomplete
                classes={autocompleteClasses}
                freeSolo
                loading={!!searching}
                loadingText={`${getTextIdTranslation('LOADING')}...`}
                clearOnBlur
                selectOnFocus
                options={getOptions()}
                value={field.value || ''}
                onChange={onSelect}
                onInputChange={onInputChange}
                getOptionLabel={(option) => (option === '0' ? '' : option)}
                renderOption={renderOption}
                getOptionDisabled={(option) => option === getTextIdTranslation('NO_OPTIONS_TO_DISPLAY')}
                filterOptions={(options) => (options.length === 0 ? [] : options)}
                renderInput={(params) => {
                  return (
                    <CustomTextField
                      fieldVariant={fieldVariant}
                      label={label}
                      {...params}
                      placeholder={updatingFields ? '' : placeholder}
                      error={error || forceAutocompleteError()}
                      // @ts-expect-error: Type '{}' is missing the following properties from type 'ControllerRenderProps<FieldValues, string>': onChange, onBlur, value, name, ref
                      field={params.inputProps}
                      inputRef={(input) => (focusAutocomplete ? input && input.focus() : input)}
                    />
                  );
                }}
              />
            );
          }}
        />
      </Collapse>
      <Collapse in={updatingFields}>
        <Box display={'flex'} gridGap={'20px'} py={2}>
          <Font variant="body2" color="textSecondary">
            <Text tId={'UPDATING_ADDRESS'} />
          </Font>
          <CircularProgress size={18} />
        </Box>
      </Collapse>
    </>
  );
};
